/**
 * Merkflow Topbar — Kora-style floating pill.
 *
 * Not `sticky`: under the dashboard layout model `<main>` is the only scroll
 * container, so the topbar sits above it and never moves. The outer wrapper
 * gives the floating-pill breathing room (margin + padding); the inner
 * <header> is the actual rounded card.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  BookOpen,
  FileText,
  Layers,
  LogOut,
  Menu,
  Search,
  Settings,
  Shield,
  Sparkles,
  User,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeToggle } from '../ui/ThemeToggle';
import { TopbarLanguageSwitcher } from '../ui/TopbarLanguageSwitcher';
import { UserAvatar } from '../ui/UserAvatar';
import { useMe, useBrands } from '@/hooks';
import { useWorkspaceContext } from '../WorkspaceProvider';
import { logout } from '@/lib/auth';
import { NotificationBell } from './NotificationBell';

interface TopbarProps {
  onMobileMenuClick: () => void;
  /** V152 — opens the Merkflow AI chat panel. The active state lives in
   *  DashboardLayout so the panel persists across route changes. */
  onChatToggle?: () => void;
  chatOpen?: boolean;
}

// Navigation pages the search can match.
// Each entry carries an i18n key — we resolve it via t() at render time so
// labels follow the current UI language and the search matches translated
// labels too (not just the English original).
const NAV_ITEMS: Array<{ i18nKey: string; path: string; icon: typeof Layers }> = [
  { i18nKey: 'nav.overview', path: '/dashboard', icon: Layers },
  { i18nKey: 'nav.posts', path: '/dashboard/posts', icon: FileText },
  { i18nKey: 'nav.review', path: '/dashboard/review', icon: FileText },
  { i18nKey: 'nav.analytics', path: '/dashboard/analytics', icon: Layers },
  { i18nKey: 'nav.engagement', path: '/dashboard/engagement', icon: Bell },
  { i18nKey: 'nav.brands', path: '/dashboard/brands', icon: BookOpen },
  { i18nKey: 'nav.storylines', path: '/dashboard/storylines', icon: BookOpen },
  { i18nKey: 'nav.discover', path: '/dashboard/discover', icon: Search },
  { i18nKey: 'nav.calendar', path: '/dashboard/calendar', icon: Layers },
  { i18nKey: 'nav.settings', path: '/dashboard/settings', icon: Settings },
  { i18nKey: 'nav.connections', path: '/dashboard/settings/connections', icon: Settings },
  { i18nKey: 'nav.members', path: '/dashboard/settings/members', icon: User },
  { i18nKey: 'nav.cadence', path: '/dashboard/settings/cadence', icon: Settings },
  { i18nKey: 'nav.billing', path: '/dashboard/settings/billing', icon: Settings },
  { i18nKey: 'nav.usage', path: '/dashboard/usage', icon: Layers },
];

interface SearchResult {
  type: 'page' | 'brand' | 'post';
  label: string;
  sublabel?: string;
  path: string;
}

export function Topbar({ onMobileMenuClick, onChatToggle, chatOpen }: TopbarProps) {
  const { t } = useTranslation();
  const { data: user } = useMe();
  const { activeWorkspace } = useWorkspaceContext();
  const workspaceId = activeWorkspace?.id ?? '';
  const navigate = useNavigate();

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: brands } = useBrands(workspaceId);

  // ⌘K / Ctrl+K to open search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Click outside to close
  useEffect(() => {
    if (!searchOpen) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [searchOpen]);

  // Compute results when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIdx(0);
      return;
    }
    const q = query.toLowerCase();
    const matched: SearchResult[] = [];

    // Match nav pages — compare against the translated label so searching
    // "Overzicht" works in NL and "Overview" works in EN.
    for (const item of NAV_ITEMS) {
      const label = t(item.i18nKey);
      if (label.toLowerCase().includes(q)) {
        matched.push({ type: 'page', label, sublabel: t('topbar.searchPage'), path: item.path });
      }
    }

    // Match brands
    for (const brand of brands ?? []) {
      if (brand.name.toLowerCase().includes(q) || brand.description?.toLowerCase().includes(q)) {
        matched.push({
          type: 'brand',
          label: brand.name,
          sublabel: t('topbar.searchBrand'),
          path: `/dashboard/brands/${brand.id}`,
        });
      }
    }

    setResults(matched.slice(0, 10));
    setSelectedIdx(0);
  }, [query, brands, t]);

  const selectResult = useCallback(
    (result: SearchResult) => {
      navigate(result.path);
      setSearchOpen(false);
      setQuery('');
    },
    [navigate],
  );

  // Keyboard navigation in results
  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIdx]) {
      e.preventDefault();
      selectResult(results[selectedIdx]);
    }
  };

  // Profile dropdown — click-away + escape-key close
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // V154 — engagement-counts polling moved into the engagement inbox
  // surface itself. The topbar bell now drives notification counts via
  // NotificationBell's own useQuery.

  return (
    <div className="flex-shrink-0 px-4 pt-4 md:pr-6">
      <header className="flex h-[60px] items-center gap-3 rounded-2xl border border-[#e5e2db] bg-white px-4 shadow-sm shadow-black/5 dark:border-white/[0.08] dark:bg-[#13131c] dark:shadow-black/20">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={onMobileMenuClick}
          className="mk-topbar-btn md:hidden"
          aria-label={t('common.openMenu')}
        >
          <Menu className="h-4 w-4" />
        </button>

        {/* Search */}
        <div className="relative flex max-w-sm min-w-0 flex-1 items-center gap-2" ref={dropdownRef}>
          <Search className="text-text-tertiary h-4 w-4 flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!searchOpen) setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={onSearchKeyDown}
            placeholder={t('topbar.searchPlaceholder')}
            className="text-text-primary placeholder:text-text-tertiary min-w-0 flex-1 border-none bg-transparent text-sm focus:outline-none"
          />
          <kbd
            className="text-text-tertiary hidden flex-shrink-0 cursor-pointer rounded-md border border-[#e5e2db] px-1.5 py-0.5 font-mono text-[10px] sm:inline-flex dark:border-white/15"
            onClick={() => {
              setSearchOpen(true);
              searchInputRef.current?.focus();
            }}
          >
            ⌘K
          </kbd>

          {/* Search results dropdown */}
          <AnimatePresence>
            {searchOpen && query.trim() && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                className="border-border absolute top-full right-0 left-0 z-50 mt-2 overflow-hidden rounded-xl border bg-white shadow-xl dark:bg-[#1a1a20]"
              >
                {results.length === 0 ? (
                  <div className="text-text-tertiary px-4 py-3 text-sm">
                    {t('topbar.searchNoResults', { query })}
                  </div>
                ) : (
                  <ul className="max-h-64 overflow-y-auto py-1">
                    {results.map((r, i) => (
                      <li key={`${r.type}-${r.path}`}>
                        <button
                          type="button"
                          onClick={() => selectResult(r)}
                          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                            i === selectedIdx
                              ? 'bg-accent/10 text-accent'
                              : 'text-text-primary hover:bg-subtle'
                          }`}
                        >
                          <span
                            className={`flex h-6 w-6 items-center justify-center rounded-lg text-[10px] font-bold ${
                              r.type === 'brand'
                                ? 'bg-accent/15 text-accent'
                                : 'bg-subtle text-text-tertiary'
                            }`}
                          >
                            {r.type === 'brand' ? 'B' : r.type === 'post' ? 'P' : '→'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">{r.label}</p>
                          </div>
                          <span className="text-text-tertiary flex-shrink-0 text-[10px] tracking-wider uppercase">
                            {r.sublabel}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex-1" />

        {/* Right cluster — tools group (theme, language, AI) on the left
            of the divider; notifications + avatar on the right. The AI
            chat icon is a tool, not a notification, so it lives with the
            tools cluster per the V152 brief. */}
        <div className="flex items-center gap-1">
          <ThemeToggle className="flex h-8 w-8 items-center justify-center rounded-xl text-[#6b6660] transition-all duration-150 hover:bg-[#f3f1ed] hover:text-[#1a1814] dark:text-white/50 dark:hover:bg-white/[0.08] dark:hover:text-white" />

          <TopbarLanguageSwitcher />

          {onChatToggle && (
            <button
              type="button"
              onClick={onChatToggle}
              aria-label={t('chatbot.open')}
              title={t('chatbot.open')}
              className={`flex h-8 w-8 items-center justify-center rounded-xl transition-all duration-150 ${
                chatOpen
                  ? 'bg-accent/10 text-accent'
                  : 'text-[#6b6660] hover:bg-[#f3f1ed] hover:text-[#1a1814] dark:text-white/50 dark:hover:bg-white/[0.08] dark:hover:text-white'
              }`}
            >
              <Sparkles className="h-4 w-4" />
            </button>
          )}

          {/* Divider — tools cluster ends here, notifications + avatar follow */}
          <div className="mx-1 h-5 w-px bg-[#e5e2db] dark:bg-white/10" />

          {/* V154 — NotificationBell replaces the navigate-to-engagement
              bell. Dropdown shows workspace notifications (cadence_failed,
              etc) with per-card Retry/Dismiss actions. Engagement inbox
              has its own sidebar link now. */}
          {workspaceId && <NotificationBell workspaceId={workspaceId} />}

          {/* Avatar + dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="hover:ring-accent/40 focus:ring-accent/40 flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-transparent transition-all focus:outline-none"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t('topbar.accountMenu')}
              title={user?.name ?? user?.email ?? t('topbar.accountFallback')}
            >
              <UserAvatar
                src={user?.avatarUrl}
                name={user?.name}
                email={user?.email}
                className="h-full w-full"
                fallbackTextClass="text-xs"
              />
            </button>

            <AnimatePresence>
              {menuOpen && (
                <motion.ul
                  initial={{ opacity: 0, y: -6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.96 }}
                  transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute top-full right-0 z-50 mt-3 w-64 space-y-1 rounded-3xl border border-white/60 bg-white/90 p-3 shadow-2xl backdrop-blur-3xl dark:border-white/10 dark:bg-[#1a1a20]/90"
                  role="menu"
                >
                  {/* User info header */}
                  <div className="border-accent/10 bg-accent/[0.06] mb-2 rounded-2xl border px-4 py-3">
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        src={user?.avatarUrl}
                        name={user?.name}
                        email={user?.email}
                        className="h-9 w-9 flex-shrink-0 rounded-xl"
                        fallbackTextClass="text-sm"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-text-primary truncate text-sm font-bold">
                          {user?.name ?? '—'}
                        </p>
                        <p className="text-text-secondary truncate text-xs">{user?.email}</p>
                      </div>
                    </div>
                    {user?.plan && (
                      <div className="mt-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase ${
                            user.systemRole === 'admin'
                              ? 'bg-purple-500/15 text-purple-600 dark:text-purple-400'
                              : user.plan === 'agency'
                                ? 'bg-accent/15 text-accent'
                                : 'bg-text-tertiary/15 text-text-tertiary'
                          }`}
                        >
                          {user.systemRole === 'admin' ? 'admin' : user.plan}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Menu items */}
                  {(
                    [
                      { label: t('nav.profile'), icon: User, to: '/dashboard/settings/profile' },
                      { label: t('nav.settings'), icon: Settings, to: '/dashboard/settings' },
                      {
                        label: t('nav.privacyData'),
                        icon: Shield,
                        to: '/dashboard/settings/privacy',
                      },
                    ] as const
                  ).map((item) => (
                    <li key={item.to}>
                      <button
                        type="button"
                        onClick={() => {
                          navigate(item.to);
                          setMenuOpen(false);
                        }}
                        className="group text-text-primary hover:bg-accent flex w-full items-center rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-150 hover:text-white"
                        role="menuitem"
                      >
                        <item.icon className="text-text-tertiary mr-3 h-4 w-4 transition-colors group-hover:text-white" />
                        {item.label}
                      </button>
                    </li>
                  ))}

                  {/* Divider */}
                  <div className="border-border my-1 border-t" />

                  {/* Log out */}
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        logout();
                      }}
                      className="flex w-full items-center rounded-xl px-4 py-2.5 text-sm font-medium text-red-500 transition-all duration-150 hover:bg-red-500/10 dark:text-red-400"
                      role="menuitem"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      {t('auth.signOut')}
                    </button>
                  </li>
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TopbarIconButton — 32px square, hover fill, transparent background by default
// ---------------------------------------------------------------------------

function TopbarIconButton({
  children,
  onClick,
  tooltip,
}: {
  children: ReactNode;
  onClick?: () => void;
  tooltip?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      className="flex h-8 w-8 items-center justify-center rounded-xl text-[#6b6660] transition-all duration-150 hover:bg-[#f3f1ed] hover:text-[#1a1814] dark:text-white/50 dark:hover:bg-white/[0.08] dark:hover:text-white"
    >
      {children}
    </button>
  );
}
